/* eslint-disable import/no-cycle */
import * as FilterStyled from '@aibees/elephant/feature/search-filters/components/filter/styles';
import { useSearchQueryFilter } from '@aibees/elephant/hooks';
import { Icon } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Typography } from '@mui/material';
import { memo, useCallback, useMemo, useState } from 'react';
import { FilterSection } from '../../components/filter-section';
import { FilterTagsHalfClosed } from '../../components/filter-tags-half-closed';
import { useFilterSections } from '../../hooks/use-filter-sections';
import { useRenderFilterContent } from '../../hooks/use-render-filter-content';
import * as Styled from './styles';
import * as SX from './sx';
import type { AccordionFilterProps as Props } from './types';

const TRANSITION_PROPS = { unmountOnExit: true };

export const AccordionFilter = memo(
  ({ className, open: defaultOpen, ...filter }: Props) => {
    const [open, setOpen] = useState(defaultOpen);

    const { mainItems, moreItems } = useFilterSections(filter);
    const selectKeys = useMemo(
      () => filter.items?.map((i) => i.field!).filter(Boolean) ?? [],
      [filter.items]
    );
    const { getParam } = useSearchQueryFilter({ select: selectKeys });

    const mainItemsContent = useRenderFilterContent(
      mainItems ?? [],
      !!open,
      filter.sections
    );

    const moreItemsContent = useRenderFilterContent(
      moreItems ?? [],
      !!open,
      filter.sections
    );

    const handleChange = useCallback(() => {
      setOpen((prev) => !prev);
    }, []);

    const isAnyOfMoreItemsSet = useMemo(() => {
      return !!moreItems?.some((i) => getParam(i.field!));
    }, [moreItems, getParam]);

    return (
      <Styled.Wrapper
        className={classnames('AccordionFilter', className, {
          open,
        })}
        sx={SX.root}
        TransitionProps={TRANSITION_PROPS}
        expanded={open}
        onChange={handleChange}
      >
        <Styled.Summary
          expandIcon={
            <Icon
              size={20}
              color={open ? 'primary' : 'default'}
              name="chevron-down"
            />
          }
        >
          <Styled.ContentSummary>
            <Styled.HeaderWrapper>
              <Styled.TitleWrapper>
                <Typography
                  variant="body2"
                  children={filter.name}
                  color="primary"
                />
              </Styled.TitleWrapper>
            </Styled.HeaderWrapper>

            <FilterTagsHalfClosed filter={filter} visible={!open} />
          </Styled.ContentSummary>
        </Styled.Summary>

        <FilterStyled.Details>
          {mainItemsContent}
          {moreItemsContent.length ? (
            <FilterSection title="More Filters" open={isAnyOfMoreItemsSet}>
              {moreItemsContent}
            </FilterSection>
          ) : null}
        </FilterStyled.Details>
      </Styled.Wrapper>
    );
  }
);

AccordionFilter.displayName = 'AccordionFilter';
